/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import type {
  Hook,
  IdentifySeriesContext,
  IdentifySeriesData,
  IdentifySeriesResult,
  LDContext,
  EvaluationSeriesContext,
  EvaluationSeriesData,
  LDEvaluationDetail,
} from "launchdarkly-js-client-sdk"
import { logRocketTrackwithObject } from "utils-lib/logRocket"

// Throttling for flag evaluations - tracks last transmission time per flag.
// This prevents us from spamming LogRocket with too many events if a flag is evaluated frequently due to re-renders, etc.
const flagThrottleMap = new Map<string, number>()
const THROTTLE_DURATION_MS = 60 * 1000 // 60 seconds

/**
 * Extracts searchable attributes from LaunchDarkly context.
 * Creates searchable attributes like:
 * - ld_context_application_key: "mywm-a60a56c8a5" (primary searchable key)
 * - ld_context_application_name: "mywm" (additional context attributes)
 * - ld_context_application_version: "a60a56c8a5"
 * - ld_context_application_anonymous: false
 * - ld_context_user_key: "00um6o6cupw9AUeVa0h7"
 * - ld_context_user_email: "ldawg2k@gmail.com"
 * - ld_context_user_anonymous: false
 * - ld_context_user_createdAt: "2025-09-24T18:28:43.988Z"
 * - ld_context_session_key: "4c7a1a1f-3aed-41c3-9ec0-6584414d2840"
 * - ld_context_session_ip: "192.0.2.1" // dummy IP address
 * - ld_context_session_anonymous: true
 * - ld_context_session_startedAt: "2025-09-24T16:34:43.679Z"
 * - ld_context_kind: "multi" (context metadata)
 */
function getContextKeyAttributes(
  context: LDContext | undefined,
): Record<string, unknown> {
  if (!context || typeof context !== "object" || context === null) return {}
  const attributes: Record<string, unknown> = {}
  const contextObj = context as Record<string, unknown>

  // Check if this is a single context (has 'kind' and 'key' at root level)
  if (contextObj.kind && contextObj.key && contextObj.kind !== "multi") {
    const kind = contextObj.kind as string
    // Primary searchable key: ld_context_{kind}_key
    attributes[`ld_context_${kind}_key`] = contextObj.key

    // Additional context attributes: ld_context_{kind}_{attribute}
    Object.keys(contextObj).forEach((childKey) => {
      if (childKey !== "key" && childKey !== "kind" && childKey !== "_meta") {
        attributes[`ld_context_${kind}_${childKey}`] = contextObj[childKey]
      }
    })

    // Include the kind itself
    attributes.ld_context_kind = contextObj.kind
  } else {
    // Multi-context: iterate through nested contexts
    Object.keys(contextObj).forEach((k) => {
      const value = contextObj[k]

      // If value is an object with a 'key', treat as context kind/key pair
      if (value && typeof value === "object" && "key" in value) {
        // Primary searchable key: ld_context_{kind}_key
        const kindKey = `ld_context_${k}_key`
        attributes[kindKey] = (value as { key?: string }).key

        // Additional context attributes: ld_context_{kind}_{attribute}
        Object.keys(value).forEach((childKey) => {
          if (childKey !== "key" && childKey !== "_meta") {
            attributes[`ld_context_${k}_${childKey}`] = (
              value as Record<string, unknown>
            )[childKey]
          }
        })
      } else {
        // For non-object values, add with ld_context prefix for consistency
        attributes[`ld_context_${k}`] = value
      }
    })
  }

  return attributes
}

const ldLogRocketLifecycleHook: Hook = {
  // This is essentially a noop but is required on the Hook interface.
  getMetadata() {
    return {
      name: "LaunchDarkly LogRocket Custom Event Hook",
    }
  },
  beforeIdentify(
    hookContext: IdentifySeriesContext,
    data: IdentifySeriesData,
  ): IdentifySeriesData {
    const eventPayload = {
      ...getContextKeyAttributes(hookContext.context),
    }
    logRocketTrackwithObject(`LaunchDarkly Before Identify`, eventPayload)
    return data
  },
  afterIdentify(
    hookContext: IdentifySeriesContext,
    data: IdentifySeriesData,
    result: IdentifySeriesResult,
  ): IdentifySeriesData {
    const eventPayload = {
      status: typeof result.status === "string" ? result.status : undefined,
      ...getContextKeyAttributes(hookContext.context),
    }
    logRocketTrackwithObject(`LaunchDarkly After Identify`, eventPayload)
    return data
  },
  afterEvaluation(
    hookContext: EvaluationSeriesContext,
    data: EvaluationSeriesData,
    detail: LDEvaluationDetail,
  ): EvaluationSeriesData {
    const flagKey = hookContext.flagKey
    const now = Date.now()

    // Check if this flag was recently transmitted
    const lastTransmissionTime = flagThrottleMap.get(flagKey)
    const shouldThrottle =
      lastTransmissionTime && now - lastTransmissionTime < THROTTLE_DURATION_MS

    if (!shouldThrottle) {
      // Update the last transmission time
      flagThrottleMap.set(flagKey, now)

      const eventPayload = {
        flagKey,
        defaultValue: hookContext.defaultValue,
        value: JSON.stringify(detail.value),
        variationIndex: detail.variationIndex,
      }
      logRocketTrackwithObject("LaunchDarkly Flag Evaluated", eventPayload)
    }

    return data
  },
}

export default ldLogRocketLifecycleHook
